import React, { useState, useMemo, useEffect } from 'react';
import { Politician, Committee } from '../api/models/models';
import { ChevronDownIcon, ChevronRightIcon, UserGroupIcon, BuildingLibraryIcon } from '@heroicons/react/24/outline';
import { congressionalApi, setTokenProvider } from '../services/congressionalApiService';
import { useAppSelector } from '../store/hooks';

interface PoliticianListComponentProps {
    onPoliticianSelect?: (politician: Politician) => void;
}

type FilterType = 'all' | 'democrat' | 'republican' | 'independent';

const PoliticianListComponent: React.FC<PoliticianListComponentProps> = ({
    onPoliticianSelect
}) => {
    const [politicians, setPoliticians] = useState<Politician[]>([]);
    const [committees, setCommittees] = useState<Committee[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [partyFilter, setPartyFilter] = useState<FilterType>('all');
    const [stateFilter, setStateFilter] = useState<string>('all');
    const [committeeFilter, setCommitteeFilter] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    const credentials = useAppSelector((state) => state.user.credentials);

    // Set up token provider
    useEffect(() => {
        setTokenProvider({
            getAccessToken: () => credentials?.accessToken || null
        });
    }, [credentials]);

    // Fetch data on component mount
    useEffect(() => {
        const fetchData = async () => {
            if (!credentials?.accessToken) {
                setError('No access token available');
                setLoading(false);
                return;
            }
            
            try {
                setLoading(true);
                const [politiciansData, committeesData] = await Promise.all([
                    congressionalApi.getPoliticians({ limit: 500 }), // Get more data for filtering
                    congressionalApi.getCommittees()
                ]);
                setPoliticians(politiciansData);
                setCommittees(committeesData);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch data');
                console.error('Error fetching congressional data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [credentials?.accessToken]);

    // Extract unique states from politicians
    const uniqueStates = useMemo(() => {
        const statesSet = new Set(politicians.map(p => p.state));
        const states = Array.from(statesSet);
        return states.sort();
    }, [politicians]);

    // Filter politicians based on search and filters
    const filteredPoliticians = useMemo(() => {
        return politicians.filter(politician => {
            // Search filter - split search term into words and match each word separately
            const matchesSearch = (() => {
                if (!searchTerm.trim()) return true;
                
                const searchWords = searchTerm.toLowerCase().trim().split(/\s+/);
                const politicianText = `${politician.name} ${politician.state} ${politician.district}`.toLowerCase();
                
                // All search words must be found somewhere in the politician text
                return searchWords.every(word => politicianText.includes(word));
            })();

            // Party filter
            const matchesParty = partyFilter === 'all' || 
                (partyFilter === 'democrat' && politician.party.toLowerCase().includes('democrat')) ||
                (partyFilter === 'republican' && politician.party.toLowerCase().includes('republican')) ||
                (partyFilter === 'independent' && !politician.party.toLowerCase().includes('democrat') && !politician.party.toLowerCase().includes('republican'));

            // State filter
            const matchesState = stateFilter === 'all' || politician.state === stateFilter;

            // Committee filter - check if politician is a member of the selected committee (directly or through subcommittees)
            const matchesCommittee = committeeFilter === 'all' || (() => {
                const selectedCommittee = committees.find(c => c.id === committeeFilter);
                if (!selectedCommittee) return false;
                
                // Check direct committee membership
                const isDirectMember = selectedCommittee.members?.some(member => 
                    member.politician?.id === politician.id
                );
                
                // Check subcommittee membership
                const isSubcommitteeMember = selectedCommittee.subcommittees?.some(subcommittee =>
                    subcommittee.members?.some(member => 
                        member.politician?.id === politician.id
                    )
                );
                
                return isDirectMember || isSubcommitteeMember;
            })();

            return matchesSearch && matchesParty && matchesState && matchesCommittee;
        });
    }, [politicians, searchTerm, partyFilter, stateFilter, committeeFilter, committees]);

    // Pagination
    const totalPages = Math.ceil(filteredPoliticians.length / itemsPerPage);
    const paginatedPoliticians = filteredPoliticians.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const getPartyColor = (party: string) => {
        if (party.toLowerCase().includes('democrat')) return 'text-blue-600 bg-blue-100';
        if (party.toLowerCase().includes('republican')) return 'text-red-600 bg-red-100';
        return 'text-gray-600 bg-gray-100';
    };

    // Loading state
    if (loading) {
        return (
            <div className="bg-white shadow-lg rounded-lg border border-neutral-300 p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                        <p className="mt-4 text-neutral-600">Loading politicians...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="bg-white shadow-lg rounded-lg border border-neutral-300 p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="text-red-600 text-xl mb-2">⚠️</div>
                        <p className="text-red-600 font-semibold">Error loading politicians</p>
                        <p className="text-neutral-600 text-sm mt-2">{error}</p>
                        <button 
                            onClick={() => window.location.reload()} 
                            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white shadow-lg rounded-lg border border-neutral-300 p-6">
            <div className="mb-6">
                <h2 className="text-2xl font-semibold text-primary-900 mb-4">
                    Politicians ({filteredPoliticians.length})
                </h2>

                {/* Search and Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    {/* Search */}
                    <input
                        type="text"
                        placeholder="Search by name, state, or district..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />

                    {/* Party Filter */}
                    <select
                        value={partyFilter}
                        onChange={(e) => setPartyFilter(e.target.value as FilterType)}
                        className="px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                        <option value="all">All Parties</option>
                        <option value="democrat">Democrat</option>
                        <option value="republican">Republican</option>
                        <option value="independent">Independent</option>
                    </select>

                    {/* State Filter */}
                    <select
                        value={stateFilter}
                        onChange={(e) => setStateFilter(e.target.value)}
                        className="px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                        <option value="all">All States</option>
                        {uniqueStates.map(state => (
                            <option key={state} value={state}>{state}</option>
                        ))}
                    </select>

                    {/* Committee Filter */}
                    <select
                        value={committeeFilter}
                        onChange={(e) => setCommitteeFilter(e.target.value)}
                        className="px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                        <option value="all">All Committees</option>
                        {committees.map(committee => (
                            <option key={committee.id} value={committee.id}>{committee.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Politicians List */}
            <div className="space-y-3">
                {paginatedPoliticians.map((politician) => (
                    <div
                        key={politician.id}
                        className="border border-neutral-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => onPoliticianSelect?.(politician)}
                    >
                        <div className="flex justify-between items-start">
                            <div className="flex-grow">
                                <h3 className="text-lg font-medium text-neutral-900">
                                    {politician.name}
                                </h3>
                                <div className="flex items-center space-x-4 text-sm text-neutral-600 mt-1">
                                    <span>{politician.state} - {politician.district}</span>
                                    <span 
                                        className={`px-2 py-1 rounded-full text-xs font-medium ${getPartyColor(politician.party)}`}
                                    >
                                        {politician.party}
                                    </span>
                                </div>
                            </div>
                            <div className="flex-shrink-0">
                                {politician.profileLink && (
                                    <a
                                        href={politician.profileLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary-600 hover:text-primary-800 text-sm"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        View Profile
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-6">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 rounded border border-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-50"
                    >
                        Previous
                    </button>
                    
                    <span className="text-sm text-neutral-600">
                        Page {currentPage} of {totalPages}
                    </span>
                    
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 rounded border border-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-50"
                    >
                        Next
                    </button>
                </div>
            )}

            {filteredPoliticians.length === 0 && (
                <div className="text-center py-8 text-neutral-500">
                    No politicians found matching your criteria.
                </div>
            )}
        </div>
    );
};

export default PoliticianListComponent;
